import React from "react";
import "../../styles/ERprotocols.css";
import { Link } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

import "../../styles/ICUProtocols.css";
const ICUProtocols = () => {
  return (
    <div className="ICU-container">
      <HelmetProvider>
        <Helmet>
          <title>
            Intensive Care Protocol for Neuromuscular Disease | Noninvasive
            Ventilation Guidelines
          </title>
          <meta
            name="description"
            content="Evidence-based intensive care protocol for patients with neuromuscular disease. Avoid unnecessary tracheostomy, manage desaturations with expiratory aids, and safely extubate to nasal ventilation."
          />
          <meta
            name="keywords"
            content="intensive care protocol, neuromuscular disease, ventilator weaning, extubation guidelines, noninvasive ventilation, mechanical insufflation-exsufflation, BiPAP, volume ventilator, SaO2 management, oxygen therapy caution"
          />
          <meta name="author" content="Millennium Respiratory Care" />{" "}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta
            property="og:image"
            content="https://doctorbach.com/images/BachJohn.jpg"
          />
          <meta
            property="og:image"
            content="https://doctorbach.com/favicon.png"
          />
          <meta
            property="og:url"
            content="https://doctorbach.com/resources/icu"
          />
          <meta property="og:type" content="article" />
        </Helmet>
      </HelmetProvider>
      <div className="precautions">
        <h3>Intensive Care Protocol</h3>
        <div className="er-pro">
          <h4>Conventional</h4>
          <ul
            style={{
              listStyleType: "none",

              paddingBottom: "0", // Remove negative padding
            }}
          >
            <li>
              <p>
                1. Oxygen administered arbitrarily in concentrations that
                maintain SaO<sub>2</sub> well above 95%.
              </p>
            </li>
            <li>
              <p>2. Frequent airway suctioning via the tube.</p>
            </li>
            <li>
              <p>3. Supplemental oxygen increased when desaturations occur.</p>
            </li>
            <li>
              <p>
                4. Ventilator weaning attempted at the expense of hypercapnia.
              </p>
            </li>
            <li>
              <p>
                5. Extubation not attempted unless the patient appears to be
                ventilator weaned.
              </p>
            </li>
            <li>
              <p>
                6. Extubation to CPAP or low span bi-level positive airway
                pressure and continued oxygen therapy.
              </p>
            </li>
            <li>
              <p>
                7. Deep airway suctioning by catheterizing the upper airway
                along with postural drainage and chest physical therapy.
              </p>
            </li>
            <li>
              <p>
                8. With increasing CO<sub>2</sub> retention or hypoxia,
                supplemental oxygen is increased and ultimately the patient is
                reintubated.
              </p>
            </li>
            <li>
              <p>
                9. Following re-intubation, tracheostomy is thought to be the
                only long-term option or following successful extubation,
                bronchodilators and ongoing routine chest physical therapy are
                used.
              </p>
            </li>
            <li>
              <p>
                10. Eventually discharged home with a tracheostomy, often
                following a rehabilitation stay for family training.
              </p>
            </li>
          </ul>
        </div>

        {/* Protocol Section */}
        <div className="er-pro">
          <h4>Protocol</h4>
          <ul>
            <li>
              <p>
                1. Oxygen administration limited only to approach 95% SaO
                <sub>2</sub>.
              </p>
            </li>
            <li>
              <p>
                2. Mechanical insufflation-exsufflation used via the tube at 25
                to 40 cm H<sub>2</sub>O to -25 to -40 cm H<sub>2</sub>O
                pressures up to every 10 minutes as needed to reverse
                oxyhemoglobin desaturations due to airway mucus accumulation and
                when there is auscultatory evidence of secretion accumulation.
                Abdominal thrusts are applied during exsufflation. Tube and
                upper airway are suctioned following use of expiratory aids as
                needed.
              </p>
            </li>
            <li>
              <p>3. Expiratory aids used when desaturations occur.</p>
            </li>
            <li>
              <p>
                4. Ventilator weaning attempted without permitting hypercapnia.
              </p>
            </li>
            <li>
              <p>
                5. Extubation attempted whether or not the patient is ventilator
                weaned when meeting the following:
              </p>
              <ul>
                <li>
                  <p>Afebrile</p>
                </li>
                <li>
                  <p>
                    No supplemental oxygen requirement to maintain SaO
                    <sub>2</sub> &gt;94%
                  </p>
                </li>
                <li>
                  <p>Chest radiograph abnormalities cleared or clearing</p>
                </li>
                <li>
                  <p>Any respiratory depressants discontinued</p>
                </li>
                <li>
                  <p>Airway suctioning required less than 1-2x/eight hours</p>
                </li>
                <li>
                  <p>
                    Coryza diminished sufficiently so that suctioning of the
                    nasal orifices is required less than once every 6 hours
                    (important to facilitate use of nasal prongs/mask for
                    post-extubation nasal ventilation)
                  </p>
                </li>
              </ul>
            </li>
            <li>
              <p>
                6. Extubation to continuous nasal ventilation and no
                supplemental oxygen.
              </p>
            </li>
            <li>
              <p>
                7. Oximetry feedback used to guide the use of expiratory aids,
                postural drainage, and chest physical therapy to reverse any
                desaturations due to airway mucus accumulation.
              </p>
            </li>
            <li>
              <p>
                8. With CO<sub>2</sub> retention or ventilator synchronization
                difficulties nasal interface leaks were eliminated, pressure
                support and ventilator rate increased or the patient switched
                from BiPAP-ST to using a volume-cycled ventilator. Persistent
                oxyhemoglobin desaturation despite eucapnia and aggressive use
                of expiratory aids indicated impending respiratory distress and
                need to re-intubate.
              </p>
            </li>
            <li>
              <p>
                9. Following re-intubation the protocol was used for a second
                trial of extubation to nasal ventilation...or following
                successful extubation, bronchodilators and chest physical
                therapy were discontinued and the patient weaned to nocturnal
                nasal ventilation.
              </p>
            </li>
            <li>
              <p>
                10. Discharge home after the SaO<sub>2</sub> remained within
                normal limits for 2 days and when assisted coughing was needed
                less than 4 times per day.
              </p>
            </li>
          </ul>
        </div>

        {/* Additional Information Section */}
        <div className="er-pro">
          <h4 style={{ margin: 0, paddingBottom: "20px" }}>
            Additional Information
          </h4>

          <p style={{ paddingLeft: "20px" }}>
            For more information about Care Protocols, click here for{" "}
            <Link
              to="/resources/op"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              Outpatient Protocols
            </Link>{" "}
            and{" "}
            <Link
              to="/resources/er"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              emergency room precautions.
            </Link>{" "}
          </p>
          <p style={{ paddingLeft: "20px" }}>
            For more information about Air Stacking or Glossopharyngeal
            Breathing (GPB),{" "}
            <Link
              to="/resources/as"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              click here.
            </Link>{" "}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ICUProtocols;
